import { useEffect, useState } from 'react';
import _ from 'lodash';
import useFetch from 'use-http';
import { API } from 'common/constants/api-urls';
import { getAccounts } from 'common/services/account.service';
import { shouldStopProjectStatesRecall } from 'common/services/http-polling-stopper';

const intervalTime = 1000 * 60;

const useProjectsStatus = ({
  withPolling = true,
  onPollingStop = _.noop
} = {}) => {
  const [projectsStatuses, setProjectsStatuses] = useState(null);
  const [shouldPollStatus, setShouldPollStatus] = useState(false);

  const { get, loading, error, response } = useFetch(
    `${API.PM_PROJECTS_VIEW}/status`
  );

  const convertAccountIdToName = async projectsState => {
    const accounts = await getAccounts();
    const accountNameById = accounts.reduce((acc, account) => {
      acc[`${account.id}`] = account.name;
      return acc;
    }, {});
    return projectsState.map(project => ({
      ...project,
      editor: project.editor ? accountNameById[project.editor] : null
    }));
  };
  const loadProjectsState = async () => {
    const projectsState = await get();
    const projectsStateWithAccounts = response.ok
      ? await convertAccountIdToName(projectsState)
      : [];

    setProjectsStatuses(projectsStateWithAccounts);
    if (withPolling && !shouldPollStatus) {
      setShouldPollStatus(true);
    }
    return projectsStateWithAccounts;
  };

  useEffect(() => {
    const asyncCall = async () => {
      const result = await loadProjectsState();
      if (!shouldStopProjectStatesRecall(result)) {
        await onPollingStop();
      }
    };
    asyncCall();
  }, []);

  useEffect(() => {
    if (!withPolling || !shouldPollStatus) {
      return;
    }

    const jobPoolingInterval = setInterval(loadProjectsState, intervalTime);
    if (projectsStatuses && shouldStopProjectStatesRecall(projectsStatuses)) {
      setShouldPollStatus(false);
      clearInterval(jobPoolingInterval);
      onPollingStop();
    }
    return () => {
      clearInterval(jobPoolingInterval);
    };
  }, [shouldPollStatus, projectsStatuses]);

  return {
    projectsStatuses,
    error,
    loading,
    getProjectsStatuses: loadProjectsState
  };
};

export default useProjectsStatus;
